import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Phone, Users as UsersIcon, Note } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const channelIcon = { call: Phone, meeting: UsersIcon, note: Note };

export default function Communications() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    student_id: "", channel: "call", summary: "", follow_up: "", follow_up_date: "",
  });

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/communications"), api.get("/students")]);
    setRows(c.data); setStudents(s.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.follow_up) delete payload.follow_up;
    if (!payload.follow_up_date) delete payload.follow_up_date;
    try {
      await api.post("/communications", payload);
      toast.success("Logged");
      setForm({ student_id: "", channel: "call", summary: "", follow_up: "", follow_up_date: "" });
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canAdd = ["admin", "principal", "teacher"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">Parent Communication</h1>
          <p className="text-zinc-500 mt-1">Log calls, meetings, and follow-ups with parents.</p>
        </div>
        {canAdd && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-comm-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" />Log Interaction</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log Parent Interaction</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label>Student</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="comm-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.class_name}-{s.section}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Channel</Label>
                  <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Phone Call</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="note">Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Summary</Label><Textarea required rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="comm-summary-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Follow-up action</Label><Input value={form.follow_up} onChange={(e) => setForm({ ...form, follow_up: e.target.value })} /></div>
                  <div><Label>Follow-up date</Label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} /></div>
                </div>
                <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="comm-submit-btn">Save</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((r) => {
          const Icon = channelIcon[r.channel] || Note;
          return (
            <Card key={r.id} className="p-5 border-zinc-200" data-testid={`comm-${r.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-indigo-50 text-indigo-700 grid place-items-center shrink-0">
                  <Icon size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold truncate">{r.student_name}</div>
                    <div className="text-xs text-zinc-500 shrink-0 ml-2">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-xs text-zinc-500 capitalize mb-1">{r.channel} · {r.teacher_name}</div>
                  <div className="text-sm text-zinc-700">{r.summary}</div>
                  {r.follow_up && (
                    <div className="mt-2 text-xs bg-amber-50 text-amber-900 rounded px-2 py-1 inline-block">
                      Follow-up: {r.follow_up} {r.follow_up_date && `· ${r.follow_up_date}`}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {rows.length === 0 && <div className="text-zinc-500 text-sm">No interactions logged yet.</div>}
      </div>
    </div>
  );
}
